import {
  Box,
  Checkbox,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import type { ReactNode } from "react";
import React, { useState, useMemo } from "react";

export type SortDirection = "asc" | "desc";

/** Props passed to filterRender for filterable columns. */
export interface FilterRenderProps {
  value: unknown;
  onChange: (value: unknown) => void;
  onClose: () => void;
  hasActive: boolean;
}

export type ColumnFilterType = "ids" | "duration" | "textMulti" | "boolean" | "checkboxSearch";

export interface Column<T> {
  id: string;
  label: ReactNode;
  sortable?: boolean;
  render: (item: T, index: number) => ReactNode;
  sortValue?: (item: T) => string | number | Date;
  /** For server-side sort; maps to API sort param. */
  sortKey?: string;
  /** Whether this column has a filter in the header. */
  filterable?: boolean;
  /** Filter type hint (for future built-in filter UIs). */
  filterType?: ColumnFilterType;
  /** Custom filter UI rendered in column header popover. */
  filterRender?: (props: FilterRenderProps & { column: Column<T> }) => ReactNode;
  /** If true, column is hidden by default (user can show via column chooser). */
  hiddenByDefault?: boolean;
}

/** Per-column filter state passed from parent (e.g. useGridFilters). */
export interface ColumnFilterProps {
  value: unknown;
  onChange: (value: unknown) => void;
  hasActive?: boolean;
}

export interface CompGridProps<T> {
  items: T[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  footer?: ReactNode;
  columns: Column<T>[];
  keyExtractor: (item: T, index: number) => string;
  isItemDimmed?: (item: T, index: number) => boolean;
  /** Visible column ids. If undefined, all columns except hiddenByDefault are shown. */
  visibleColumns?: string[];
  /** Called when user toggles column visibility. */
  onVisibleColumnsChange?: (columnIds: string[]) => void;
  /** Controlled sort column (for server-side sort). */
  sortColumn?: string | null;
  /** Controlled sort direction. */
  sortDirection?: SortDirection;
  /** Called when user clicks sort. Use for server-side sort. */
  onSortChange?: (columnId: string, direction: SortDirection) => void;
  /** When true, skip client-side sort (parent handles via API). */
  serverSort?: boolean;
  /** Filter props per column id. Required for filterable columns. */
  columnFilterProps?: Record<string, ColumnFilterProps>;
}

function getDefaultVisibleColumns<T>(cols: Column<T>[]): string[] {
  const hasHidden = cols.some((c) => c.hiddenByDefault);
  if (!hasHidden) return cols.map((c) => c.id);
  return cols.filter((c) => !c.hiddenByDefault).map((c) => c.id);
}

export function CompGrid<T>({
  items,
  loading = false,
  error = null,
  emptyMessage = "No items found",
  footer,
  columns,
  keyExtractor,
  isItemDimmed,
  visibleColumns: visibleColumnsProp,
  onVisibleColumnsChange,
  sortColumn: sortColumnProp,
  sortDirection: sortDirectionProp,
  onSortChange,
  serverSort = false,
  columnFilterProps,
}: CompGridProps<T>) {
  const [internalSortColumn, setInternalSortColumn] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>("asc");
  const [filterAnchor, setFilterAnchor] = useState<{ el: HTMLElement; columnId: string } | null>(
    null,
  );
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);

  const sortColumn = sortColumnProp ?? internalSortColumn;
  const sortDirection = sortDirectionProp ?? internalSortDirection;

  const visibleColumnIds = useMemo(() => {
    if (visibleColumnsProp !== undefined) return visibleColumnsProp;
    return getDefaultVisibleColumns(columns);
  }, [visibleColumnsProp, columns]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleColumnIds.includes(c.id)),
    [columns, visibleColumnIds],
  );

  const sortedItems = useMemo(() => {
    if (serverSort || !sortColumn) return items;

    const column = columns.find((col) => col.id === sortColumn);
    if (!column || !column.sortable || !column.sortValue) return items;

    const sorted = [...items].sort((a, b) => {
      const aVal = column.sortValue!(a);
      const bVal = column.sortValue!(b);

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [items, sortColumn, sortDirection, columns, serverSort]);

  const handleSort = (columnId: string) => {
    if (onSortChange) {
      const nextDir =
        sortColumn === columnId && sortDirection === "asc" ? "desc" : "asc";
      onSortChange(columnId, nextDir);
    } else {
      if (sortColumn === columnId) {
        setInternalSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setInternalSortColumn(columnId);
        setInternalSortDirection("asc");
      }
    }
  };

  const handleColumnVisibilityToggle = (columnId: string, checked: boolean) => {
    if (!onVisibleColumnsChange) return;
    const next = checked
      ? [...visibleColumnIds, columnId]
      : visibleColumnIds.filter((id) => id !== columnId);
    onVisibleColumnsChange(next);
  };

  const hasColumnChooser =
    !!onVisibleColumnsChange && columns.length > 1;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  const activeFilterColumnId = filterAnchor?.columnId ?? null;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ bgcolor: "transparent", width: "100%", maxHeight: "70vh", overflow: "auto" }}
    >
      <Table size="small" sx={{ width: "100%" }} stickyHeader>
        <TableHead>
          <TableRow>
            {visibleColumns.map((column) => {
              const filterProps = columnFilterProps?.[column.id];
              const hasFilter = column.filterable && column.filterRender && filterProps;
              const hasActiveFilter = filterProps?.hasActive ?? false;

              return (
                <TableCell key={column.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      width: "100%",
                    }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortColumn === column.id}
                        direction={sortColumn === column.id ? sortDirection : "asc"}
                        onClick={() => handleSort(column.id)}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ flex: 1, fontWeight: 500 }}
                      >
                        {column.label}
                      </Typography>
                    )}
                    {hasFilter && (
                      <IconButton
                        size="small"
                        onClick={(e) =>
                          setFilterAnchor({ el: e.currentTarget, columnId: column.id })
                        }
                        color={hasActiveFilter ? "primary" : "default"}
                        sx={{ p: 0.25 }}
                        aria-label={`Filter ${String(column.label)}`}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              );
            })}
            {hasColumnChooser && (
              <TableCell padding="none" sx={{ width: 48, verticalAlign: "middle" }}>
                <IconButton
                  size="small"
                  onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                  aria-label="Column visibility"
                >
                  <ViewColumnIcon fontSize="small" />
                </IconButton>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length + (hasColumnChooser ? 1 : 0)}
                sx={{ textAlign: "center" }}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            sortedItems.map((item, index) => {
              const dimmed = isItemDimmed ? isItemDimmed(item, index) : false;
              return (
                <TableRow
                  key={keyExtractor(item, index)}
                  sx={{
                    opacity: dimmed ? 0.3 : 1,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {visibleColumns.map((column) => (
                    <TableCell key={column.id}>{column.render(item, index)}</TableCell>
                  ))}
                  {hasColumnChooser && <TableCell padding="none" sx={{ width: 48 }} />}
                </TableRow>
              );
            })
          )}
          {footer}
        </TableBody>
      </Table>

      {activeFilterColumnId && (() => {
        const column = columns.find((c) => c.id === activeFilterColumnId);
        const filterProps = columnFilterProps?.[activeFilterColumnId];
        if (!column?.filterRender || !filterProps) return null;

        return (
          <Popover
            open={!!filterAnchor}
            anchorEl={filterAnchor?.el}
            onClose={() => setFilterAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { sx: { p: 2, minWidth: 220 } } }}
          >
            {column.filterRender({
              value: filterProps.value,
              onChange: filterProps.onChange,
              onClose: () => setFilterAnchor(null),
              hasActive: filterProps.hasActive ?? false,
              column,
            })}
          </Popover>
        );
      })()}

      {hasColumnChooser && (
        <Popover
          open={!!columnMenuAnchor}
          anchorEl={columnMenuAnchor}
          onClose={() => setColumnMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { maxHeight: 320 } } }}
        >
          <List dense sx={{ py: 0 }}>
            {columns.map((col) => (
              <ListItemButton
                key={col.id}
                dense
                onClick={() =>
                  handleColumnVisibilityToggle(col.id, !visibleColumnIds.includes(col.id))
                }
              >
                <Checkbox
                  checked={visibleColumnIds.includes(col.id)}
                  size="small"
                  sx={{ mr: 1 }}
                  tabIndex={-1}
                  disableRipple
                />
                <Typography variant="body2">{col.label}</Typography>
              </ListItemButton>
            ))}
          </List>
        </Popover>
      )}
    </TableContainer>
  );
}
